import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Tier } from '@backrs_ui/common-ui';

import {
  CareerPathWithScore,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
  SchoolLevel,
} from '@backrs_ui/grow-ui/src/models/models';
import { ScoreCalculationResult } from '@backrs_ui/grow-ui/src/utils/calculateSurveyResults';

import { v4 as uuidv4 } from 'uuid';

import {
  getMostRecentUserResponseSetFromAPI,
  getUserResponseSetFromAPI,
  postAPISurveyResults,
  postAPISurveyResultsNewUser,
} from './thunks/userResponse.thunk';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

type UserResponseState = Readonly<{
  /** The user's survey responses as they're filling out the survey */
  userResponseSet: CareerUserResponseSet;
  /** The results from the users survey, or the most recent survey results from the API (both are the same) */
  mostRecentUserResponseSet?: CareerUserResponseSet | null;
  /** Career Path score results */
  results?: {
    tier: Tier;
    careerPathSuggestions: CareerPathWithScore[];
    careerResults: ScoreCalculationResult;
  };
  loadingMostRecentUserResponseSet: boolean;
  loading: boolean;
  error: string | null;
}>;

const initialState: UserResponseState = {
  userResponseSet: {
    id: uuidv4(),
    user_responses: [],
  },
  mostRecentUserResponseSet: null,
  loadingMostRecentUserResponseSet: true,
  loading: false,
  error: null,
};

export const userResponseSetSlice = createSlice({
  name: 'userResponseSet',
  initialState,
  reducers: {
    resetUserResponseSet: (state) => {
      return {
        ...state,
        userResponseSet: initialState.userResponseSet,
      };
    },
    addUsersSchoolLevel: (state, action: PayloadAction<SchoolLevel>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          school_level: action.payload,
        },
      };
    },
    addUserQuestionResponse: (state, action: PayloadAction<CareerUserResponseAnswer>) => {
      const updatedResponse = action.payload;
      const existingResponseIndex = state.userResponseSet.user_responses.findIndex(
        (response) => response.id === updatedResponse.id,
      );

      if (existingResponseIndex !== -1) {
        return {
          ...state,
          userResponseSet: {
            ...state.userResponseSet,
            user_responses: state.userResponseSet.user_responses.map((response, index) =>
              index === existingResponseIndex ? updatedResponse : response,
            ),
          },
        };
      }

      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          user_responses: [...state.userResponseSet.user_responses, updatedResponse],
        },
      };
    },
    addUserSurveyResults: (
      state,
      action: PayloadAction<{
        tier: Tier;
        careerPathSuggestions: CareerPathWithScore[];
        careerResults: ScoreCalculationResult;
      }>,
    ) => {
      return {
        ...state,
        results: action.payload,
      };
    },
    updateResponseSetWithAPIResponse: (state, action: PayloadAction<CareerUserResponseSet>) => {
      return {
        ...state,
        userResponseSet: action.payload,
      };
    },
    setMostRecentResponseLoading: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loadingMostRecentUserResponseSet: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // POST API SURVEY RESULTS
      .addCase(postAPISurveyResults.fulfilled, (state, action) => {
        return {
          ...state,
          userResponseSet: initialState.userResponseSet, // reset the user response state after submitting
          mostRecentUserResponseSet: action.payload.userResponseSet,
          results: action.payload.results,
          loadingMostRecentUserResponseSet: false,
          loading: false,
        };
      })
      .addCase(postAPISurveyResults.rejected, (state) => {
        return {
          ...state,
          loading: false,
          error: 'Failed to submit survey results',
          loadingMostRecentUserResponseSet: false,
        };
      })
      .addCase(postAPISurveyResults.pending, (state) => {
        return {
          ...state,
          loading: true,
          error: null,
          loadingMostRecentUserResponseSet: false,
        };
      })

      // GET SURVEY RESULTS FROM API BY ID
      .addCase(getUserResponseSetFromAPI.pending, (state) => {
        return {
          ...state,
          loading: true,
          error: null,
        };
      })

      .addCase(getUserResponseSetFromAPI.fulfilled, (state, action) => {
        return {
          ...state,
          userResponseSet: action.payload.userResponseSet,
          results: action.payload.results,
          loading: false,
        };
      })

      .addCase(getUserResponseSetFromAPI.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          error: action.payload as string,
        };
      })

      // GET MOST SURVEY RESULTS FROM API
      .addCase(getMostRecentUserResponseSetFromAPI.pending, (state) => {
        return {
          ...state,
          loadingMostRecentUserResponseSet: true,
          error: null,
        };
      })

      .addCase(getMostRecentUserResponseSetFromAPI.fulfilled, (state, action) => {
        return {
          ...state,
          mostRecentUserResponseSet: action.payload.userResponseSet,
          results: action.payload.results,
          loadingMostRecentUserResponseSet: false,
        };
      })

      .addCase(getMostRecentUserResponseSetFromAPI.rejected, (state, action) => {
        return {
          ...state,
          loadingMostRecentUserResponseSet: false,
          error: action.payload as string,
        };
      })

      // POST API SURVEY RESULTS UNAUTHENTICATED
      .addCase(postAPISurveyResultsNewUser.fulfilled, (state, action) => {
        return {
          ...state,
          userResponseSet: initialState.userResponseSet, // reset the user response state after submitting
          mostRecentUserResponseSet: action.payload.userResponseSet,
          loadingMostRecentUserResponseSet: false,
          results: action.payload.results,
        };
      })
      .addCase(postAPISurveyResultsNewUser.rejected, (state) => {
        return {
          ...state,
          loading: false,
          error: 'Failed to submit survey results',
          loadingMostRecentUserResponseSet: false,
        };
      })
      .addCase(postAPISurveyResultsNewUser.pending, (state) => {
        return {
          ...state,
          loading: true,
          loadingMostRecentUserResponseSet: false,
          error: null,
        };
      })

      // LOGOUT
      .addCase(logout, (state) => {
        state = initialState;

        return state;
      });
  },
});

// Other code such as selectors can use the imported `RootState` type
export const selectUserResponseSet = (state: RootState) => state.userResponseSet.userResponseSet;
export const selectUserSurveyResults = (state: RootState) => state.userResponseSet.results;

export const userResponseSetReducer = userResponseSetSlice.reducer;

export const {
  addUserQuestionResponse,
  addUsersSchoolLevel,
  addUserSurveyResults,
  updateResponseSetWithAPIResponse,
  resetUserResponseSet,
  setMostRecentResponseLoading,
} = userResponseSetSlice.actions;
